<!-- User button -->
<button mat-icon-button (click)="openPanel()" #userOrigin>
    <span class="relative">
        @if (showAvatar && user?.avatar) {
            <img class="h-7 w-7 rounded-full" [src]="user.avatar" />
        }
        @if (!showAvatar || !user?.avatar) {
            <mat-icon [svgIcon]="'heroicons_outline:user-circle'"></mat-icon>
        }
        <span
            class="absolute bottom-0 right-0 h-2 w-2 rounded-full"
            [ngClass]="{
                'mb-px mr-px': !showAvatar || !user?.avatar,
                'bg-[#0097A9]': user?.status === 'online',
                'bg-[#F48F1F]': user?.status === 'away',
                'bg-red-500': user?.status === 'busy',
                'bg-gray-400': user?.status === 'not-visible',
            }"
        ></span>
    </span>
</button>

<!-- User panel -->
<ng-template #userPanel>
    <div
        class="fixed inset-0 flex flex-col overflow-hidden shadow-lg sm:static sm:inset-auto sm:w-72 sm:rounded-2xl"
    >
        <!-- Header -->
        <div class="flex shrink-0 items-center bg-[#0E2B58] px-6 py-4 text-white">
            <div class="-ml-1 mr-3 sm:hidden">
                <button mat-icon-button (click)="closePanel()">
                    <mat-icon
                        class="text-current icon-size-5"
                        [svgIcon]="'heroicons_solid:x-mark'"
                    ></mat-icon>
                </button>
            </div>
            <div class="flex items-center">
                <div class="flex h-10 w-10 items-center justify-center rounded-full bg-[#0097A9] mr-3">
                    <mat-icon class="text-white icon-size-5" [svgIcon]="'heroicons_outline:user'"></mat-icon>
                </div>
                <div class="flex flex-col">
                    <span class="text-base font-medium leading-none">{{ user?.name }}</span>
                    <span class="mt-1 text-sm opacity-80">{{ user?.email }}</span>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div class="bg-card flex flex-col">
            <!-- Menu items -->
            <button
                class="flex items-center px-6 py-3 hover:bg-[#0097A9]/5"
                (click)="closePanel()"
            >
                <mat-icon
                    class="icon-size-5 mr-3 text-[#0097A9]"
                    [svgIcon]="'heroicons_outline:user-circle'"
                ></mat-icon>
                <span>Profile</span>
            </button>

            <button
                class="flex items-center px-6 py-3 hover:bg-[#0097A9]/5"
                (click)="closePanel()"
            >
                <mat-icon
                    class="icon-size-5 mr-3 text-[#0097A9]"
                    [svgIcon]="'heroicons_outline:cog-8-tooth'"
                ></mat-icon>
                <span>Settings</span>
            </button>

            <mat-divider class="my-2"></mat-divider>

            <!-- Theme toggle -->
            <button
                class="flex items-center justify-between px-6 py-3 hover:bg-[#0097A9]/5"
                (click)="toggleScheme()"
            >
                <div class="flex items-center">
                    <mat-icon
                        class="icon-size-5 mr-3 text-[#0097A9]"
                        [svgIcon]="scheme === 'dark' ? 'heroicons_outline:moon' : 'heroicons_outline:sun'"
                    ></mat-icon>
                    <span>{{ scheme === 'dark' ? 'Dark Mode' : 'Light Mode' }}</span>
                </div>
                <div class="flex items-center gap-2">
                    <span class="text-xs text-gray-500">{{ scheme === 'dark' ? 'ON' : 'OFF' }}</span>
                    <div
                        class="w-10 h-5 rounded-full relative transition-colors duration-200"
                        [class.bg-[#0097A9]]="scheme === 'dark'"
                        [class.bg-gray-300]="scheme !== 'dark'"
                    >
                        <div
                            class="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                            [class.translate-x-5]="scheme === 'dark'"
                            [class.translate-x-0.5]="scheme !== 'dark'"
                        ></div>
                    </div>
                </div>
            </button>

            <!-- User / Admin nav mode toggle (only for admin users) -->
            @if (hasAdminAccess()) {
                <button
                    class="flex items-center justify-between px-6 py-3 hover:bg-[#0097A9]/5"
                    (click)="toggleNavMode()"
                >
                    <div class="flex items-center">
                        <mat-icon
                            class="icon-size-5 mr-3 text-[#0097A9]"
                            [svgIcon]="navMode() === 'admin' ? 'heroicons_outline:cog-6-tooth' : 'heroicons_outline:chart-bar'"
                        ></mat-icon>
                        <span>{{ navMode() === 'admin' ? 'Admin' : 'User' }} Mode</span>
                    </div>
                    <div class="flex items-center gap-2">
                        <span class="text-xs px-2 py-0.5 rounded"
                            [ngClass]="{
                                'bg-amber-100 text-amber-700': navMode() === 'admin',
                                'bg-sky-100 text-sky-700': navMode() === 'user'
                            }"
                        >{{ navMode() === 'admin' ? 'ADMIN' : 'USER' }}</span>
                        <div
                            class="w-10 h-5 rounded-full relative transition-colors duration-200"
                            [ngClass]="{
                                'bg-amber-500': navMode() === 'admin',
                                'bg-sky-500': navMode() === 'user'
                            }"
                        >
                            <div
                                class="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                                [class.translate-x-5]="navMode() === 'admin'"
                                [class.translate-x-0.5]="navMode() !== 'admin'"
                            ></div>
                        </div>
                    </div>
                </button>
            }

            <!-- Status section -->
            <!-- <div class="px-6 py-3">
                <span class="text-secondary text-sm font-medium">Status</span>
            </div>

            <button
                class="flex items-center px-6 py-2 hover:bg-[#0097A9]/5"
                (click)="updateUserStatus('online')"
            >
                <span class="mr-3 h-3 w-3 rounded-full bg-[#0097A9]"></span>
                <span>Online</span>
            </button>

            <button
                class="flex items-center px-6 py-2 hover:bg-[#0097A9]/5"
                (click)="updateUserStatus('away')"
            >
                <span class="mr-3 h-3 w-3 rounded-full bg-[#F48F1F]"></span>
                <span>Away</span>
            </button>

            <button
                class="flex items-center px-6 py-2 hover:bg-[#0097A9]/5"
                (click)="updateUserStatus('busy')"
            >
                <span class="mr-3 h-3 w-3 rounded-full bg-red-500"></span>
                <span>Busy</span>
            </button>

            <button
                class="flex items-center px-6 py-2 hover:bg-[#0097A9]/5"
                (click)="updateUserStatus('not-visible')"
            >
                <span class="mr-3 h-3 w-3 rounded-full bg-gray-400"></span>
                <span>Invisible</span>
            </button> -->

            <mat-divider class="my-2"></mat-divider>

            <!-- SignalR Connection Test -->
            <button
                class="flex items-center justify-between px-6 py-3 hover:bg-[#0097A9]/5"
                (click)="testSignalRConnection()"
                [disabled]="isTestingConnection()"
            >
                <div class="flex items-center">
                    <mat-icon
                        class="icon-size-5 mr-3"
                        [ngClass]="{
                            'text-green-500': signalRState() === 'Connected',
                            'text-amber-500': signalRState() === 'Connecting' || signalRState() === 'Reconnecting',
                            'text-red-500': signalRState() === 'Disconnected'
                        }"
                        [svgIcon]="getSignalRStatusIcon()"
                    ></mat-icon>
                    <span>SignalR</span>
                </div>
                <div class="flex items-center gap-2">
                    <span
                        class="text-xs px-2 py-0.5 rounded"
                        [ngClass]="{
                            'bg-green-100 text-green-700': signalRState() === 'Connected',
                            'bg-amber-100 text-amber-700': signalRState() === 'Connecting' || signalRState() === 'Reconnecting',
                            'bg-red-100 text-red-700': signalRState() === 'Disconnected'
                        }"
                    >
                        {{ getSignalRStatusText() }}
                    </span>
                    @if (isTestingConnection()) {
                        <mat-icon class="icon-size-4 animate-spin text-[#0097A9]" [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
                    }
                </div>
            </button>

            <mat-divider class="my-2"></mat-divider>

            <!-- Sign out -->
            <button
                class="flex items-center px-6 py-3 hover:bg-red-50 text-red-600"
                (click)="signOut()"
            >
                <mat-icon
                    class="icon-size-5 mr-3"
                    [svgIcon]="'heroicons_outline:arrow-right-on-rectangle'"
                ></mat-icon>
                <span>Sign out</span>
            </button>
        </div>
    </div>
</ng-template>